'use client';

import { useMemo, useState } from 'react';
import {
  BEZIER_PRESETS,
  CLIP_PATH_LABELS,
  bezierCurvePath,
  bezierCss,
  bezierValue,
  checkboxSnippet,
  clipPathCss,
  clipPathValue,
  patternCss,
  patternPreviewStyle,
  switchDimensions,
  switchSnippet,
  type BezierState,
  type ClipPathShape,
  type CssVisualVariant,
  type PatternKind,
} from '@/lib/browser-cssvisual-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const META: Record<CssVisualVariant, { badge: string; title: string; subtitle: string }> = {
  'css-clip-path-generator': {
    badge: 'Clip-path',
    title: 'CSS clip-path generator',
    subtitle: 'Pick a shape or type polygon points, preview it live, and copy the clip-path CSS.',
  },
  'css-cubic-bezier-generator': {
    badge: 'Easing',
    title: 'CSS cubic-bezier generator',
    subtitle: 'Tune the four control points, watch the animated preview, and copy the easing CSS.',
  },
  'css-background-pattern-generator': {
    badge: 'Pattern',
    title: 'CSS background pattern generator',
    subtitle: 'Build gradient-based patterns with color and size controls, then copy the CSS.',
  },
  'css-checkbox-generator': {
    badge: 'Checkbox',
    title: 'CSS checkbox generator',
    subtitle:
      'Style a custom checkbox with color, size, and radius controls, then copy HTML + CSS.',
  },
  'css-switch-generator': {
    badge: 'Switch',
    title: 'CSS toggle switch generator',
    subtitle: 'Design an iOS-style toggle with on/off colors and size, then copy HTML + CSS.',
  },
};

const CLIP_SHAPES: ClipPathShape[] = [
  'triangle',
  'circle',
  'ellipse',
  'hexagon',
  'pentagon',
  'message',
  'arrow-right',
  'close',
  'frame',
  'custom',
];

const PATTERN_KINDS: PatternKind[] = ['stripes', 'checkerboard', 'dots', 'diagonal', 'grid'];

export default function BrowserCssVisualGenerator({ variant }: { variant: CssVisualVariant }) {
  const meta = META[variant];
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  // clip-path
  const [clipShape, setClipShape] = useState<ClipPathShape>('hexagon');
  const [clipPoints, setClipPoints] = useState('50% 0%, 0% 100%, 100% 100%');
  const [clipOnImage, setClipOnImage] = useState(false);

  // cubic-bezier
  const [bezier, setBezier] = useState<BezierState>(BEZIER_PRESETS.ease);
  const [bezierDuration, setBezierDuration] = useState(1.5);

  // background pattern
  const [patternKind, setPatternKind] = useState<PatternKind>('stripes');
  const [patFg, setPatFg] = useState('#6366f1');
  const [patBg, setPatBg] = useState('#eef2ff');
  const [patSize, setPatSize] = useState(40);

  // checkbox
  const [cbSize, setCbSize] = useState(24);
  const [cbRadius, setCbRadius] = useState(6);
  const [cbColor, setCbColor] = useState('#6366f1');
  const [cbBorder, setCbBorder] = useState('#cbd5e1');

  // switch
  const [swWidth, setSwWidth] = useState(56);
  const [swOn, setSwOn] = useState('#34c759');
  const [swOff, setSwOff] = useState('#e5e7eb');

  const output = useMemo(() => {
    if (variant === 'css-clip-path-generator') {
      return clipPathCss(clipShape, clipPoints);
    }
    if (variant === 'css-cubic-bezier-generator') {
      return bezierCss(bezier, bezierDuration);
    }
    if (variant === 'css-background-pattern-generator') {
      return patternCss({ kind: patternKind, color1: patFg, color2: patBg, size: patSize });
    }
    if (variant === 'css-checkbox-generator') {
      return checkboxSnippet({
        size: cbSize,
        radius: cbRadius,
        color: cbColor,
        borderColor: cbBorder,
      });
    }
    return switchSnippet({ width: swWidth, onColor: swOn, offColor: swOff });
  }, [
    variant,
    clipShape,
    clipPoints,
    bezier,
    bezierDuration,
    patternKind,
    patFg,
    patBg,
    patSize,
    cbSize,
    cbRadius,
    cbColor,
    cbBorder,
    swWidth,
    swOn,
    swOff,
  ]);

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  function updateBezier(key: keyof BezierState, value: number) {
    setBezier((prev) => ({ ...prev, [key]: value }));
  }

  const sw = switchDimensions(swWidth);

  const isCode = variant === 'css-checkbox-generator' || variant === 'css-switch-generator';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">{meta.subtitle}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'css-clip-path-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Shape</span>
                  <select
                    onChange={(e) => setClipShape(e.target.value as ClipPathShape)}
                    value={clipShape}
                  >
                    {CLIP_SHAPES.map((shape) => (
                      <option key={shape} value={shape}>
                        {CLIP_PATH_LABELS[shape]}
                      </option>
                    ))}
                  </select>
                </label>
                {clipShape === 'custom' ? (
                  <label className="tool-live-field tool-live-field-full">
                    <span>Polygon points</span>
                    <textarea
                      onChange={(e) => setClipPoints(e.target.value)}
                      rows={3}
                      value={clipPoints}
                    />
                  </label>
                ) : null}
                <label className="tool-live-field">
                  <span>Preview on image</span>
                  <input
                    checked={clipOnImage}
                    onChange={(e) => setClipOnImage(e.target.checked)}
                    type="checkbox"
                  />
                </label>
              </>
            ) : null}

            {variant === 'css-cubic-bezier-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Preset</span>
                  <select
                    onChange={(e) => {
                      const preset = BEZIER_PRESETS[e.target.value];
                      if (preset) setBezier(preset);
                    }}
                    value={
                      Object.keys(BEZIER_PRESETS).find(
                        (name) => bezierValue(BEZIER_PRESETS[name]) === bezierValue(bezier),
                      ) ?? ''
                    }
                  >
                    <option value="">Custom</option>
                    {Object.keys(BEZIER_PRESETS).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                {(['x1', 'y1', 'x2', 'y2'] as (keyof BezierState)[]).map((key) => (
                  <label className="tool-live-field" key={key}>
                    <span>
                      {key}: {bezier[key]}
                    </span>
                    <input
                      max={key === 'x1' || key === 'x2' ? 1 : 2}
                      min={key === 'x1' || key === 'x2' ? 0 : -1}
                      onChange={(e) => updateBezier(key, Number(e.target.value))}
                      step={0.01}
                      type="range"
                      value={bezier[key]}
                    />
                  </label>
                ))}
                <label className="tool-live-field">
                  <span>Duration: {bezierDuration}s</span>
                  <input
                    max={4}
                    min={0.2}
                    onChange={(e) => setBezierDuration(Number(e.target.value))}
                    step={0.1}
                    type="range"
                    value={bezierDuration}
                  />
                </label>
              </>
            ) : null}

            {variant === 'css-background-pattern-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Pattern</span>
                  <select
                    onChange={(e) => setPatternKind(e.target.value as PatternKind)}
                    value={patternKind}
                  >
                    {PATTERN_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {kind}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Foreground</span>
                  <input onChange={(e) => setPatFg(e.target.value)} type="color" value={patFg} />
                </label>
                <label className="tool-live-field">
                  <span>Background</span>
                  <input onChange={(e) => setPatBg(e.target.value)} type="color" value={patBg} />
                </label>
                <label className="tool-live-field">
                  <span>Size: {patSize}px</span>
                  <input
                    max={120}
                    min={8}
                    onChange={(e) => setPatSize(Number(e.target.value))}
                    type="range"
                    value={patSize}
                  />
                </label>
              </>
            ) : null}

            {variant === 'css-checkbox-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Size: {cbSize}px</span>
                  <input
                    max={48}
                    min={14}
                    onChange={(e) => setCbSize(Number(e.target.value))}
                    type="range"
                    value={cbSize}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Radius: {cbRadius}px</span>
                  <input
                    max={24}
                    min={0}
                    onChange={(e) => setCbRadius(Number(e.target.value))}
                    type="range"
                    value={cbRadius}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Checked color</span>
                  <input
                    onChange={(e) => setCbColor(e.target.value)}
                    type="color"
                    value={cbColor}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Border color</span>
                  <input
                    onChange={(e) => setCbBorder(e.target.value)}
                    type="color"
                    value={cbBorder}
                  />
                </label>
              </>
            ) : null}

            {variant === 'css-switch-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Width: {swWidth}px</span>
                  <input
                    max={96}
                    min={36}
                    onChange={(e) => setSwWidth(Number(e.target.value))}
                    type="range"
                    value={swWidth}
                  />
                </label>
                <label className="tool-live-field">
                  <span>On color</span>
                  <input onChange={(e) => setSwOn(e.target.value)} type="color" value={swOn} />
                </label>
                <label className="tool-live-field">
                  <span>Off color</span>
                  <input onChange={(e) => setSwOff(e.target.value)} type="color" value={swOff} />
                </label>
              </>
            ) : null}
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            {variant === 'css-clip-path-generator' ? (
              <div
                style={{
                  width: 180,
                  height: 180,
                  clipPath: clipPathValue(clipShape, clipPoints),
                  WebkitClipPath: clipPathValue(clipShape, clipPoints),
                  // "Preview on image" has to be an actual image: a clip path behaves
                  // differently over photographic detail than over a flat gradient.
                  background: clipOnImage
                    ? 'url(/og/about.jpg) center / cover no-repeat'
                    : 'linear-gradient(135deg, #6366f1, #ec4899)',
                }}
              />
            ) : null}

            {variant === 'css-cubic-bezier-generator' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%' }}>
                <style>{`@keyframes cleanorBezierRun{0%{left:0}100%{left:calc(100% - 28px)}}`}</style>
                <svg
                  height={160}
                  style={{ width: 160, margin: '0 auto', overflow: 'visible' }}
                  viewBox="0 0 160 160"
                  width={160}
                >
                  <rect fill="rgba(99,102,241,0.06)" height={160} width={160} x={0} y={0} />
                  <line
                    stroke="rgba(148,163,184,0.5)"
                    strokeWidth={1}
                    x1={0}
                    x2={160}
                    y1={160}
                    y2={0}
                  />
                  <path
                    d={bezierCurvePath(bezier, 160)}
                    fill="none"
                    stroke="#6366f1"
                    strokeWidth={3}
                  />
                </svg>
                <div
                  style={{
                    position: 'relative',
                    height: 28,
                    width: '100%',
                    background: 'rgba(99,102,241,0.08)',
                    borderRadius: 14,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: 0,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: '#6366f1',
                      animation: `cleanorBezierRun ${bezierDuration}s ${bezierValue(
                        bezier,
                      )} infinite alternate`,
                    }}
                  />
                </div>
              </div>
            ) : null}

            {variant === 'css-background-pattern-generator' ? (
              <div
                style={{
                  width: '100%',
                  height: 200,
                  borderRadius: 12,
                  ...patternPreviewStyle({
                    kind: patternKind,
                    color1: patFg,
                    color2: patBg,
                    size: patSize,
                  }),
                }}
              />
            ) : null}

            {variant === 'css-checkbox-generator' ? (
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  color: '#0f172a',
                }}
              >
                <span
                  style={{
                    position: 'relative',
                    width: cbSize,
                    height: cbSize,
                    borderRadius: cbRadius,
                    background: cbColor,
                    border: `2px solid ${cbColor}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      width: Math.max(3, Math.round(cbSize * 0.22)),
                      height: Math.max(6, Math.round(cbSize * 0.5)),
                      borderRight: '2px solid #fff',
                      borderBottom: '2px solid #fff',
                      transform: 'rotate(45deg) translateY(-10%)',
                    }}
                  />
                </span>
                <span>Label</span>
              </label>
            ) : null}

            {variant === 'css-switch-generator' ? (
              <span
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: sw.width,
                  height: sw.height,
                  background: swOn,
                  borderRadius: sw.height / 2,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 3,
                    left: 3,
                    width: sw.knob,
                    height: sw.knob,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    transform: `translateX(${sw.travel}px)`,
                  }}
                />
              </span>
            ) : null}
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>{isCode ? 'HTML + CSS' : 'CSS'}</span>
            <textarea readOnly rows={isCode ? 14 : 4} value={output} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
